/**
 * Today's date in IST as "YYYY-MM-DD". Used anywhere the codebase needs
 * "what's today's date" — filename stamps, default date-range filters,
 * "is this in the past?" comparisons. The naive `new Date().toISOString()`
 * returns UTC, which between 00:00 and 05:30 IST returns YESTERDAY's date.
 * That bit us in the Email Logs default range (rows submitted at 00:30 IST
 * went missing from the filter window until ~05:30 IST). Use this helper
 * instead — same shape, always IST.
 */
const _istDateFmt = new Intl.DateTimeFormat("en-CA", {
  timeZone: "Asia/Kolkata",
  year: "numeric", month: "2-digit", day: "2-digit",
});
export const todayIST = () => _istDateFmt.format(new Date());
export const daysAgoIST = (n) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return _istDateFmt.format(d);
};
// Current month in IST as "YYYY-MM" (for month-precision validations).
const _istMonthFmt = new Intl.DateTimeFormat("en-CA", {
  timeZone: "Asia/Kolkata",
  year: "numeric", month: "2-digit",
});
export const currentMonthIST = () => _istMonthFmt.format(new Date());

/**
 * Strip the time portion from any ISO date string or YYYY-MM-DD string.
 * Returns just "YYYY-MM-DD". Safe for <input type="date"> values and display.
 * Returns "" for null/undefined, returns the original value if already short.
 */
export const fmtDay = (d) => (d ? String(d).slice(0, 10) : "");

/**
 * Format an ISO timestamp as "Mon DD, YYYY HH:MM AM/PM" in IST (Asia/Kolkata).
 * The backend stores everything in IST (see config/db.js timezone "+05:30") so
 * the moment-in-time the API returns must be displayed in IST too — otherwise
 * the UI shows a 5h30m drift relative to what the DB actually has.
 *
 *   "2026-05-22T10:48:29.000Z" (= 16:18 IST) → "May 22, 2026 04:18 PM"
 *
 * Uses Intl.DateTimeFormat with `timeZone: "Asia/Kolkata"` so it's correct
 * regardless of which timezone the user's browser is set to.
 */
export const fmtDateTime = (d) => {
  if (!d) return "";
  const date = new Date(d);
  if (isNaN(date)) return String(d);
  // Build the parts in IST and assemble manually to keep the exact format
  // the rest of the app already renders (no locale-specific punctuation).
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: "Asia/Kolkata",
    month: "short", day: "numeric", year: "numeric",
    hour: "numeric", minute: "2-digit", hour12: true,
  }).formatToParts(date);
  const get = (type) => (parts.find(p => p.type === type) || {}).value || "";
  const mon = get("month");
  const day = get("day");
  const yr = get("year");
  const hh = String(get("hour")).padStart(2, "0");
  const mm = get("minute");
  const ampm = get("dayPeriod").toUpperCase();
  return `${mon} ${day}, ${yr} ${hh}:${mm} ${ampm}`;
};

/**
 * Format a date as "DD Mon YYYY" for human-readable display.
 * Used in email previews so the deadline shows same as actual sent email.
 * e.g. "2026-03-31" or "2026-03-31T00:00:00.000Z" → "31 Mar 2026"
 */
export const fmtDisplayDate = (d) => {
  if (!d) return "";
  const s = String(d).slice(0, 10);
  const date = new Date(s + "T00:00:00");
  if (isNaN(date)) return s;
  return date.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
};